import React, { useState, useEffect, useMemo, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  SELLABLE_PLAN_DEFS,
  PlanId,
  BillingPeriod,
  AccountUnit,
  bill,
  fmt,
  totalSuffix,
} from './subscriptionModel';
import {
  cancelSubscription,
  resumeSubscription,
  previewUpgradeProration,
  upgradeProration,
  isPaymentsConfigured,
  listBranches,
} from '../services/paymentsApi';
import { customToast } from '../utils/customToast';
import {
  V, SectionHead, Chip, PlanCard, AddonRow, CycleSwitch, SummaryBlock,
  PrimaryButton, GhostButton,
} from './SubscriptionUI';
import AddBranchPanel from './AddBranchPanel';

/* ============================================================================
   SubscriptionManager — the Manage-Subscription modal, rebuilt to the approved
   940px design (plan cards, add-on toggle rows, live billing summary).

   The billing logic underneath is unchanged and still the source of truth:
     • upgrade  → real prorated Paystack charge (Apply now), hosted fallback
     • downgrade/lateral → applied immediately at the customer's request
     • either   → can instead be scheduled to the next cycle
     • cancel   → cancel-at-period-end, with a resume path

   TIER-AWARE PRIMARY ACTION
   -------------------------
   Enterprise is the top tier, so "upgrade" is meaningless there. For an org
   already on Enterprise the primary action becomes "Add a branch" — a whole
   additional organization under the same account, billed on its own size and
   prorated onto this org's renewal date so the group shares one cycle.
   ========================================================================== */

const CANCEL_REASONS = [
  'Too expensive',
  'Missing features we need',
  'Switching to another provider',
  'School / fleet is closing',
  'Only needed it temporarily',
  'Other',
] as const;

interface Props {
  orgCode: string;
  orgData: {
    subscriptionPlan?: string;
    addons?: Record<string, { wanted: boolean; status: string }>;
    billingPeriod?: string;
    expirationDate?: any;
    productType?: string;
    plannedChildren?: number;
    plannedBuses?: number;
    name?: string;
    enterpriseGroupId?: string;
    branchOrgCodes?: string[];
    parentOrgCode?: string;
  };
  onClose: () => void;
  /** After a branch is created — switch session into it (Admin SDK via payments). */
  onBranchCreated?: (branchOrgCode: string) => void | Promise<void>;
}

interface PendingChange {
  plan: string;
  addons: Record<string, boolean>;
  billingPeriod?: string;
  effectiveDate: string;
  requestedAt: string;
}

const ADDON_KEYS = ['gps', 'fuel', 'fees'] as const;
type AddonKey = (typeof ADDON_KEYS)[number];

const ADDON_META: Record<AddonKey, { label: string; desc: string; schoolOnly?: boolean }> = {
  gps: { label: 'GPS Tracking', desc: 'Live location on every vehicle.' },
  fuel: { label: 'Fuel Monitoring', desc: 'Track consumption and flag siphoning.' },
  fees: { label: 'School Fees', desc: 'Collect and reconcile fees in-app.', schoolOnly: true },
};

const PLAN_ORDER: PlanId[] = ['pilot', 'basic', 'premium', 'enterprise'];
const PAID_PLANS = SELLABLE_PLAN_DEFS;

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function SubscriptionManager({ orgCode, orgData, onClose, onBranchCreated }: Props) {
  const isSchool = orgData.productType !== 'CARGO' && orgData.productType !== 'SELF_TRACKING';
  const unit: AccountUnit = isSchool ? 'child' : 'vehicle';
  const size = isSchool ? (orgData.plannedChildren || 0) : (orgData.plannedBuses || 0);
  const currentPeriod = (orgData.billingPeriod as BillingPeriod) || (isSchool ? 'termly' : 'month');

  const initAddons: Record<string, boolean> = {};
  for (const key of ADDON_KEYS) initAddons[key] = orgData.addons?.[key]?.wanted ?? false;

  const storedPlan = (orgData.subscriptionPlan as PlanId) || 'premium';
  // Picker no longer offers Basic — start on Premium for legacy Basic orgs.
  // Upgrade/dirty math still uses storedPlan so Basic → Premium is a real upgrade.
  const currentPlan: PlanId = storedPlan === 'basic' ? 'premium' : storedPlan;
  const [selectedPlan, setSelectedPlan] = useState<string>(currentPlan);
  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod>(currentPeriod);
  const [selectedAddons, setSelectedAddons] = useState<Record<string, boolean>>(initAddons);
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [branchOpen, setBranchOpen] = useState(false);
  const branchSectionRef = useRef<HTMLElement | null>(null);
  const branchCreateRef = useRef<(() => void) | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  /* The cancel panel opens at the BOTTOM of a scrolling modal, and the button
   * that opens it sits in the fixed footer — so tapping "Cancel subscription"
   * appeared to do nothing until you scrolled down and found the reason box
   * (Gabriel, 2026-08-13). Bring it into view instead: a control that responds
   * off-screen reads as broken, and the second thing a user does is press it
   * again. */
  const cancelPanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!cancelOpen) return;
    // After paint, or the panel has no layout to scroll to yet.
    const id = window.requestAnimationFrame(() => {
      cancelPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [cancelOpen]);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [cancelNote, setCancelNote] = useState('');
  const [canceling, setCanceling] = useState(false);
  const [cancelPending, setCancelPending] = useState(false);

  function openBranchForm() {
    setBranchOpen(true);
    // Footer lives outside the scroll body — without this, opening the form is invisible.
    window.requestAnimationFrame(() => {
      branchSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'organizations', orgCode));
        const data = snap.data();
        setPendingChange(data?.pendingPlanChange || null);
        setCancelPending(data?.cancelAtPeriodEnd === true);
      } finally {
        setLoading(false);
      }
    })();
  }, [orgCode]);

  const nextBillingDate: Date = useMemo(() => {
    const exp = orgData.expirationDate;
    if (exp && typeof exp.toDate === 'function') return exp.toDate();
    if (exp instanceof Date) return exp;
    const fallback = new Date();
    fallback.setDate(fallback.getDate() + 30);
    return fallback;
  }, [orgData.expirationDate]);

  const currentAddonCount = Object.values(orgData.addons || {}).filter((a) => a.wanted).length;
  const newAddonCount = Object.values(selectedAddons).filter(Boolean).length;

  // An Enterprise school pays by its place in its group: 750 on the first 3
  // campuses, 850 from the 4th. The mother is campus 1; a branch asks the
  // server, which reads the mother's list (the branch may not).
  const [campus, setCampus] = useState(1);
  useEffect(() => {
    if (!orgData.parentOrgCode) { setCampus(1); return undefined; }
    let live = true;
    listBranches(orgCode).then((r) => {
      const mine = r.ok ? (r.orgs || []).find((o) => o.code === orgCode) : undefined;
      if (live) setCampus(mine?.campus || 1);
    });
    return () => { live = false; };
  }, [orgCode, orgData.parentOrgCode]);
  const currentTotal = bill(storedPlan, size, unit, currentPeriod, currentAddonCount, campus);
  const newTotal = bill(selectedPlan as PlanId, size, unit, selectedPeriod, newAddonCount, campus);
  const diff = newTotal - currentTotal;

  // Basic → Premium is the retired-plan migration (same as cron): free, not a
  // prorated upsell. Basic → Enterprise still charges as a real upgrade.
  const isRetirementMigrate = storedPlan === 'basic' && selectedPlan === 'premium';
  const isUpgrade = !isRetirementMigrate
    && PLAN_ORDER.indexOf(selectedPlan as PlanId) > PLAN_ORDER.indexOf(storedPlan);
  // Already at the ceiling: there is nothing to upgrade TO, so the modal's job
  // changes from "pick a bigger plan" to "add another branch".
  const isEnterprise = storedPlan === 'enterprise';
  const dirty = selectedPlan !== storedPlan
    || selectedPeriod !== currentPeriod
    || ADDON_KEYS.some((k) => (initAddons[k] ?? false) !== (selectedAddons[k] ?? false));

  const suffix = (p: BillingPeriod) => `/${totalSuffix(unit, p)}`;
  const sfx = suffix(selectedPeriod);
  const ksh = (n: number) => `Ksh ${fmt(n)}`;
  // What one add-on adds per cycle. It is split the same way as the plan (a
  // school's year over 12 months, 10% off a year paid up front), so the flat
  // 50 × size — a term's worth — only matched its label on a per-term school.
  const addonPerCycle = bill(selectedPlan as PlanId, size, unit, selectedPeriod, 1, campus)
    - bill(selectedPlan as PlanId, size, unit, selectedPeriod, 0, campus);

  // Schools get the real term-calendar cycle; lorry/fleet bill monthly.
  const cycleOptions = isSchool
    ? ([
      { id: 'termly' as BillingPeriod, label: 'Termly', hint: 'Charged at the start of each term' },
      { id: 'month' as BillingPeriod, label: 'Monthly' },
      { id: 'year' as BillingPeriod, label: 'Annual', hint: 'Save 10%' },
    ])
    : ([
      { id: 'month' as BillingPeriod, label: 'Monthly' },
      { id: 'year' as BillingPeriod, label: 'Annual', hint: 'Save 10%' },
    ]);

  async function handleSchedule() {
    setSaving(true);
    try {
      const change: PendingChange = {
        plan: selectedPlan,
        addons: { ...selectedAddons },
        billingPeriod: selectedPeriod,
        effectiveDate: nextBillingDate.toISOString(),
        requestedAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, 'organizations', orgCode), { pendingPlanChange: change });
      setPendingChange(change);
      customToast.show(`Scheduled for ${formatDate(nextBillingDate)}.`, 'success');
    } finally {
      setSaving(false);
    }
  }

  async function persistAddons() {
    const updates: Record<string, any> = {};
    for (const key of ADDON_KEYS) updates[`addons.${key}.wanted`] = selectedAddons[key] ?? false;
    await updateDoc(doc(db, 'organizations', orgCode), updates);
  }

  async function handleApplyNow() {
    setSaving(true);
    try {
      // DOWNGRADE / lateral change → no charge; applies immediately at the
      // customer's request. (Scheduling a downgrade uses handleSchedule.)
      if (!isUpgrade) {
        await updateDoc(doc(db, 'organizations', orgCode), {
          subscriptionPlan: selectedPlan,
          billingPeriod: selectedPeriod,
          pendingPlanChange: null,
          ...Object.fromEntries(ADDON_KEYS.map((k) => [`addons.${k}.wanted`, selectedAddons[k] ?? false])),
        });
        customToast.show(`You're now on ${selectedPlan}.`, 'success');
        onClose();
        return;
      }

      const plan = selectedPlan as 'premium' | 'enterprise';

      // LOCAL / dev without the payments backend: apply directly so the
      // dashboard stays testable. In production REACT_APP_PAYMENTS_URL is set,
      // so every upgrade goes through the real prorated charge below.
      if (!isPaymentsConfigured()) {
        await updateDoc(doc(db, 'organizations', orgCode), {
          subscriptionPlan: selectedPlan,
          billingPeriod: selectedPeriod,
          pendingPlanChange: null,
          ...Object.fromEntries(ADDON_KEYS.map((k) => [`addons.${k}.wanted`, selectedAddons[k] ?? false])),
        });
        customToast.show(`Upgraded to ${plan} (dev mode — no charge).`, 'success');
        onClose();
        return;
      }

      const preview = await previewUpgradeProration(orgCode, plan);
      if (!preview.ok) {
        customToast.show(preview.message || 'Could not compute the upgrade amount.', 'error');
        return;
      }
      const proceed = window.confirm(
        `Upgrade to ${plan} now for Ksh ${(preview.amountKES ?? 0).toLocaleString()}?\n\n`
        + `This is a prorated charge for the ${preview.daysRemaining} day(s) left in your current period. `
        + `Your full ${plan} rate applies from your next renewal.`,
      );
      if (!proceed) return;

      const result = await upgradeProration(orgCode, plan);
      if (result.ok) {
        await persistAddons();
        customToast.show(`Upgraded to ${plan}. Zana is waking up — insights begin shortly.`, 'success');
        onClose();
      } else if (result.fallback === 'hosted' && result.authorization_url) {
        window.location.href = result.authorization_url;
      } else {
        customToast.show(result.message || 'Upgrade failed. Please try another method.', 'error');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleCancelPending() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'organizations', orgCode), { pendingPlanChange: null });
      setPendingChange(null);
    } finally {
      setSaving(false);
    }
  }

  async function handleCancelSubscription() {
    if (!cancelReason) {
      customToast.show('Please tell us why you are cancelling — it helps us improve.', 'error');
      return;
    }
    setCanceling(true);
    const reason = cancelNote.trim() ? `${cancelReason} — ${cancelNote.trim()}` : cancelReason;
    const result = await cancelSubscription(orgCode, reason);
    setCanceling(false);
    if (result.ok) {
      setCancelPending(true);
      setCancelOpen(false);
      customToast.show(
        `Cancellation scheduled. You keep full access until ${formatDate(nextBillingDate)} and won't be billed again.`,
        'success',
      );
    } else {
      customToast.show(result.message || 'Could not cancel. Please try again or contact support.', 'error');
    }
  }

  async function handleResumeSubscription() {
    setCanceling(true);
    const result = await resumeSubscription(orgCode);
    setCanceling(false);
    if (result.ok) {
      setCancelPending(false);
      customToast.show('Welcome back — your subscription will continue as normal.', 'success');
    } else {
      customToast.show(result.message || 'Could not resume. Please contact support.', 'error');
    }
  }

  const cycleWord = selectedPeriod === 'year' ? 'Annual'
    : selectedPeriod === 'month' ? 'Monthly'
      : selectedPeriod === 'term' ? 'Quarterly' : 'Termly';
  const branchCount = (orgData.branchOrgCodes || []).length;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(24,20,45,.46)', zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '32px 16px', overflowY: 'auto',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: 'min(940px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        background: '#fff', borderRadius: 24, overflow: 'hidden',
        boxShadow: '0 40px 90px -30px rgba(30,25,60,.45), 0 8px 24px rgba(30,25,60,.12)',
      }}>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{ flexShrink: 0, padding: '26px 32px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', color: V.ink }}>
                Manage subscription
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {orgData.name && <Chip tone="violet">{orgData.name}</Chip>}
                <Chip>{storedPlan.charAt(0).toUpperCase() + storedPlan.slice(1)}</Chip>
                <Chip>{cycleWord}</Chip>
                {branchCount > 0 && <Chip tone="violet">{branchCount} branch{branchCount === 1 ? '' : 'es'}</Chip>}
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                flexShrink: 0, width: 36, height: 36, border: 'none', borderRadius: '50%',
                background: '#f3f2f6', color: '#6b6b7b', fontSize: 16, lineHeight: 1,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >✕</button>
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '4px 32px 30px',
          display: 'flex', flexDirection: 'column', gap: 30,
        }}>
          {loading ? (
            <p style={{ color: V.muted, textAlign: 'center', padding: '48px 0' }}>Loading…</p>
          ) : (
            <>
              {cancelPending && (
                <Notice tone="red" title="Cancellation scheduled">
                  You keep full access until <strong>{formatDate(nextBillingDate)}</strong> and won't be
                  billed again. After that date your organization closes and everyone is signed out.
                  <div style={{ marginTop: 10 }}>
                    <SmallButton onClick={handleResumeSubscription} disabled={canceling} tone="green">
                      {canceling ? 'Working…' : 'Keep my subscription'}
                    </SmallButton>
                  </div>
                </Notice>
              )}

              {pendingChange && (
                <Notice tone="amber" title="Change scheduled">
                  On <strong>{formatDate(new Date(pendingChange.effectiveDate))}</strong> your plan switches
                  to <strong>{pendingChange.plan.charAt(0).toUpperCase() + pendingChange.plan.slice(1)}</strong> with{' '}
                  <strong>{Object.values(pendingChange.addons).filter(Boolean).length} add-on(s)</strong>.
                  <div style={{ marginTop: 10 }}>
                    <SmallButton onClick={handleCancelPending} disabled={saving} tone="amber">
                      Cancel scheduled change
                    </SmallButton>
                  </div>
                </Notice>
              )}

              {/* ── Billing cycle ── */}
              <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <SectionHead
                  label="Billing cycle"
                  note={selectedPeriod === 'year' ? '10% off when billed annually' : undefined}
                />
                <CycleSwitch value={selectedPeriod} options={cycleOptions} onChange={setSelectedPeriod} />
              </section>

              {/* ── Plans ── */}
              <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <SectionHead
                  label="Choose plan"
                  note={`${size} ${unit === 'child' ? 'child' : 'vehicle'}${size === 1 ? '' : 's'} on your account`}
                />
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 16, alignItems: 'stretch', marginTop: 14,
                }}>
                  {PAID_PLANS.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      name={plan.name}
                      price={ksh(bill(plan.id, size, unit, selectedPeriod, newAddonCount, campus))}
                      suffix={sfx}
                      desc={plan.desc}
                      feats={plan.feats}
                      selected={selectedPlan === plan.id}
                      recommended={plan.rec}
                      current={currentPlan === plan.id}
                      onSelect={() => setSelectedPlan(plan.id)}
                    />
                  ))}
                </div>
              </section>

              {/* ── Add-ons ── */}
              <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <SectionHead label="Add-ons" note="Optional · billed with your plan" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
                  {ADDON_KEYS.filter((k) => !(ADDON_META[k].schoolOnly && !isSchool)).map((key) => (
                    <AddonRow
                      key={key}
                      label={ADDON_META[key].label}
                      desc={ADDON_META[key].desc}
                      price={`+${ksh(addonPerCycle)} ${sfx}`}
                      on={selectedAddons[key] ?? false}
                      onToggle={() => setSelectedAddons((p) => ({ ...p, [key]: !p[key] }))}
                    />
                  ))}
                </div>
              </section>

              {/* ── Enterprise: add a branch (mother only — children stay one level deep) ── */}
              {isEnterprise && !orgData.parentOrgCode && (
                <section
                  ref={branchSectionRef}
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  <SectionHead label="Branches" note="Each branch is billed on its own size" />
                  <AddBranchPanel
                    motherOrgCode={orgCode}
                    motherName={orgData.name || orgCode}
                    unit={unit}
                    renewsOn={nextBillingDate}
                    open={branchOpen}
                    onOpen={openBranchForm}
                    onCancel={() => setBranchOpen(false)}
                    registerCreate={(run) => { branchCreateRef.current = run; }}
                    onCreated={async (branchCode) => {
                      onClose();
                      if (branchCode && onBranchCreated) await onBranchCreated(branchCode);
                    }}
                  />
                </section>
              )}

              {/* ── Summary ── */}
              <SummaryBlock
                currentLabel={`${ksh(currentTotal)} ${suffix(currentPeriod)}`}
                newLabel={`${ksh(newTotal)} ${sfx}`}
                change={diff}
                changeLabel={`${diff > 0 ? '+' : '−'}${ksh(Math.abs(diff))} ${sfx}`}
                footnote={
                  isUpgrade
                    ? `Apply now charges the prorated difference today; the full rate starts ${formatDate(nextBillingDate)}.`
                    : `Changes take effect at your next billing cycle · ${formatDate(nextBillingDate)}`
                }
              />

              {cancelOpen && (
                <div ref={cancelPanelRef}>
                <CancelPanel
                  reason={cancelReason}
                  note={cancelNote}
                  canceling={canceling}
                  renewsOn={formatDate(nextBillingDate)}
                  onReason={setCancelReason}
                  onNote={setCancelNote}
                  onKeep={() => setCancelOpen(false)}
                  onConfirm={handleCancelSubscription}
                />
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        {!loading && (
          <div style={{
            flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 16, padding: '18px 32px', borderTop: `1px solid ${V.hair}`, background: '#fff',
          }}>
            {!cancelPending && !cancelOpen ? (
              <button
                onClick={() => setCancelOpen(true)}
                style={{
                  border: 'none', background: 'none', font: 'inherit', fontSize: 13, fontWeight: 600,
                  color: '#a3a3b0', cursor: 'pointer', padding: 0,
                }}
              >Cancel subscription</button>
            ) : <span />}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <GhostButton onClick={onClose} disabled={saving}>Close</GhostButton>

              {/* Enterprise has nothing to upgrade to — its primary action is
                  adding another branch, not scheduling a tier change. A plan or
                  cycle edit still gets its own scheduling button when dirty. */}
              {isEnterprise && !dirty ? (
                !orgData.parentOrgCode ? (
                  <PrimaryButton
                    onClick={() => {
                      if (branchOpen && branchCreateRef.current) {
                        branchCreateRef.current();
                      } else {
                        openBranchForm();
                      }
                    }}
                    disabled={saving}
                  >
                    {branchOpen ? 'Create branch' : '+ Add a branch'}
                  </PrimaryButton>
                ) : null
              ) : (
                <>
                  {dirty && (
                    <GhostButton onClick={handleSchedule} disabled={saving}>
                      {saving ? 'Saving…' : 'Schedule for next cycle'}
                    </GhostButton>
                  )}
                  <PrimaryButton onClick={handleApplyNow} disabled={saving || !dirty}>
                    {saving ? 'Working…' : isUpgrade ? 'Upgrade & pay difference' : 'Apply now'}
                  </PrimaryButton>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── local presentational bits ──────────────────────────────────────────── */

function Notice({ tone, title, children }: { tone: 'red' | 'amber'; title: string; children: React.ReactNode }) {
  const c = tone === 'red'
    ? { bg: '#fef2f2', border: '#fecaca', head: '#991b1b', body: '#7f1d1d' }
    : { bg: '#fffbeb', border: '#fde68a', head: '#92400e', body: '#78350f' };
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 14, padding: '14px 16px' }}>
      <p style={{ margin: '0 0 6px', fontSize: 13, color: c.head, fontWeight: 700 }}>{title}</p>
      <div style={{ fontSize: 13, color: c.body, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

function SmallButton({
  children, onClick, disabled, tone,
}: { children: React.ReactNode; onClick: () => void; disabled?: boolean; tone: 'green' | 'amber' }) {
  const c = tone === 'green'
    ? { fg: '#166534', bg: '#f0fdf4', border: '#bbf7d0' }
    : { fg: '#b45309', bg: 'transparent', border: '#fcd34d' };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: 12, fontWeight: 700, color: c.fg, background: c.bg,
        border: `1px solid ${c.border}`, borderRadius: 8, padding: '6px 12px',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
      }}
    >{children}</button>
  );
}

function CancelPanel({
  reason, note, canceling, renewsOn, onReason, onNote, onKeep, onConfirm,
}: {
  reason: string; note: string; canceling: boolean; renewsOn: string;
  onReason: (v: string) => void; onNote: (v: string) => void;
  onKeep: () => void; onConfirm: () => void;
}) {
  return (
    <div style={{ background: '#fff7f7', border: '1px solid #fecaca', borderRadius: 14, padding: '16px 18px' }}>
      <p style={{ margin: '0 0 6px', fontSize: 13.5, fontWeight: 700, color: '#991b1b' }}>
        Cancel your subscription?
      </p>
      <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#7f1d1d', lineHeight: 1.55 }}>
        You won't be billed for the next cycle, and you keep <b>full access until {renewsOn}</b>. Amounts
        already billed aren't refunded. After that date your organization closes and everyone is signed
        out. Organization data is permanently wiped within <b>30 days</b> after that date (billing
        records may be kept where the law requires). See our{' '}
        <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#7f1d1d' }}>billing terms</a>.
      </p>
      <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700, color: '#7f1d1d' }}>Why are you leaving?</p>
      <select
        value={reason}
        onChange={(e) => onReason(e.target.value)}
        style={{
          width: '100%', padding: '9px 11px', borderRadius: 10, border: '1px solid #fca5a5',
          fontSize: 13, marginBottom: 8, background: '#fff', color: '#374151',
        }}
      >
        <option value="">Choose a reason…</option>
        {CANCEL_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <textarea
        value={note}
        onChange={(e) => onNote(e.target.value)}
        placeholder="Anything else we should know? (optional)"
        rows={2}
        style={{
          width: '100%', padding: '9px 11px', borderRadius: 10, border: '1px solid #fca5a5',
          fontSize: 13, marginBottom: 10, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
        }}
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <GhostButton onClick={onKeep} disabled={canceling}>Keep my subscription</GhostButton>
        <button
          onClick={onConfirm}
          disabled={canceling}
          style={{
            background: '#dc2626', color: '#fff', border: 'none', borderRadius: 12,
            padding: '12px 20px', fontSize: 14, fontWeight: 800,
            cursor: canceling ? 'not-allowed' : 'pointer', opacity: canceling ? 0.7 : 1,
          }}
        >{canceling ? 'Cancelling…' : 'Confirm cancellation'}</button>
      </div>
    </div>
  );
}
